// @flow
import React from 'react';
import {
  getAccordianPanelDetailsObject,
  getAccordionsKeyFromPanelsArray,
} from '@src/common-utils/utils';
import { AccordionsList } from '@presentational/accordions/AccordionsList';
import { SPUserCommitGenericDetailsContainer } from '@streaming-projects/user-commit-details/generic-details/SPUserCommitGenericDetailsContainer';
import { useFormikContext } from 'formik';
import { BACKEND_FIELDNAMES, FORM_NAMES } from '@streaming-projects/enums';
import { SPCreditsAndBurstsContainer } from '@streaming-projects/credits-and-bursts/SPCreditsAndBurstsContainer';
import {
  useCreateSPUserCommitCNBCMutation,
  useDeleteSPUserCommitCNBCMutation,
  useUpdateSPUserCommitCNBCMutation,
} from '@streaming-projects/service-definitions/streamingProjectsApi';
import useIsAdmin from '@streaming-projects/useIsAdmin';
import { StyledLabel } from '@src/common-utils/styledComponents';
import { Spacer } from '@presentational/spacing/Spacer';
import {
  IS_CNBC_SECTION_LIVE,
  IS_COMMIT_CUSTOM_VIEWS_ENABLED,
  IS_MLD_IN_COMMIT_LIVE,
  IS_USER_COMMITS_DISCOUNTS_PUSH_TO_SFDC_LIVE,
} from '@streaming-projects/constants';
import useDoesUserHaveEarlyAccess from '@streaming-projects/useDoesUserHaveEarlyAccess';
import { isAnythingEditedBesidesGivenFormBasedOnGivenConfig } from '@streaming-projects/utils';
import { SPUserCommitDiscountsTopLevelContainer } from '@streaming-projects/user-commit-details/accordians-container/SPUserCommitDiscountsTopLevelContainer';
import { SPUserCommitCommittedCapacityConfigurationContainer } from '@streaming-projects/user-commit-details/committed-capacity/SPUserCommitCommittedCapacityConfigurationContainer';

import { SPUserCommitSFDCDetailsContainer } from '../sfdc-details/SPUserCommitSFDCDetailsContainer';
import { SPUserCommitAmountSection } from '../commit-amount/SPUserCommitAmountSection';
import { SPUserCommitSPSelectionContainerWrapper } from '../sp-selection/SPUserCommitSPSelectionContainerWrapper';
import { useSPUserCommitDetailsContext } from '../contexts/SPUserCommitDetailsContext';
// CTODO:SP Remove import not needed after backend changes
import { SPUserCommitSummaryContainerWithTabs } from '../commit-summary/SPUserCommitSummaryContainer';
import { SPUserCommitSummaryContainer } from '../monthly-summary/SPUserCommitSummaryContainer';
import { SP_USER_COMMIT_PAGE_CONFIG } from '../config';

import { SPPushDiscountsToSFDCBtn } from './SPPushDiscountsToSFDCBtn';

export const SPUserCommitDetailsAccordians = () => {
  const { initialValues, values } = useFormikContext();
  const doesUserHaveEarlyAccess = useDoesUserHaveEarlyAccess();
  const isUserAdmin = useIsAdmin();

  const shouldCreditAndBurstsBeDisabled = isAnythingEditedBesidesGivenFormBasedOnGivenConfig(
    initialValues,
    values,
    SP_USER_COMMIT_PAGE_CONFIG,
    FORM_NAMES.CREDIT_AND_BURSTS.CREDITS_AND_BURSTS_FORM
  );

  const accordionPanels = [
    getAccordianPanelDetailsObject('Commit Details', <SPUserCommitGenericDetailsContainer />),
  ];

  if (
    IS_CNBC_SECTION_LIVE &&
    doesUserHaveEarlyAccess &&
    initialValues?.[BACKEND_FIELDNAMES.CREDITS_AND_BURSTS_ENABLED]
  ) {
    accordionPanels.push(
      getAccordianPanelDetailsObject(
        'Credits And Bursts',
        <SPCreditsAndBurstsContainer
          createMutationToUse={useCreateSPUserCommitCNBCMutation}
          deleteMutationToUse={useDeleteSPUserCommitCNBCMutation}
          disabled={shouldCreditAndBurstsBeDisabled}
          getContextData={useSPUserCommitDetailsContext}
          readOnlyMode={!isUserAdmin}
          updateMutationToUse={useUpdateSPUserCommitCNBCMutation}
        />
      )
    );
  }

  if (initialValues?.[BACKEND_FIELDNAMES.COMMITTED_CAPACITY]) {
    accordionPanels.push(
      getAccordianPanelDetailsObject(
        'Committed Capacity',
        <SPUserCommitCommittedCapacityConfigurationContainer />
      )
    );
  }

  if (IS_MLD_IN_COMMIT_LIVE) {
    accordionPanels.push(
      getAccordianPanelDetailsObject('Discounts', <SPUserCommitDiscountsTopLevelContainer />)
    );
  }

  accordionPanels.push(
    getAccordianPanelDetailsObject(
      IS_USER_COMMITS_DISCOUNTS_PUSH_TO_SFDC_LIVE && doesUserHaveEarlyAccess ? (
        <>
          <StyledLabel basic={true} content="SFDC Contract Details" />
          <Spacer x={10} />
          <SPPushDiscountsToSFDCBtn />
        </>
      ) : (
        'SFDC Contract Details'
      ),
      <SPUserCommitSFDCDetailsContainer />,
      'SFDC Contract Details'
    )
  );

  accordionPanels.push(
    getAccordianPanelDetailsObject('SP Selection', <SPUserCommitSPSelectionContainerWrapper />)
  );
  accordionPanels.push(
    getAccordianPanelDetailsObject('Commit Amount', <SPUserCommitAmountSection />)
  );

  accordionPanels.push(
    IS_COMMIT_CUSTOM_VIEWS_ENABLED
      ? getAccordianPanelDetailsObject('Commit Summary', <SPUserCommitSummaryContainerWithTabs />)
      : getAccordianPanelDetailsObject(
          'User Commit Pricing Summary',
          <SPUserCommitSummaryContainer />
        )
  );

  const accordionKey = getAccordionsKeyFromPanelsArray(accordionPanels);

  return (
    <AccordionsList
      key={accordionKey}
      panels={accordionPanels}
      showOnlyTitleInCollapsedState={true}
    />
  );
};
